import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Target, X } from "lucide-react";
import { API } from "@/lib/api";

/**
 * MissionCard — a persistent, coach-voice nudge card shown at the top of the
 * athlete dashboard when there's a pending mission.
 *
 * Props:
 *   mission: { mission_id, type, body }
 *   onStart: () => void          — user clicked "Start now" (opens workouts tab)
 *   onResolved: () => void       — fires after dismiss or complete to refresh state
 */
export const MissionCard = ({ mission, onStart, onResolved }) => {
  const [isClosing, setIsClosing] = useState(false);

  if (!mission || isClosing) return null;

  const titleMap = {
    inactivity: "Sachin's mission for you",
    friday_nudge: "One more session",
    first_workout: "Let's get you started",
    coach_check_in: `Note from ${mission.coach_name || "your coach"}`,
  };

  const ctaMap = {
    inactivity: "Start now",
    friday_nudge: "Jump in",
    first_workout: "Pick a program",
    coach_check_in: "Start now",
  };

  const title = titleMap[mission.type] || "Sachin's mission for you";
  const cta = ctaMap[mission.type] || "Start now";

  const handleDismiss = async () => {
    setIsClosing(true);
    try {
      await fetch(`${API}/athlete/missions/${mission.mission_id}/dismiss`, {
        method: "POST",
        credentials: "include",
      });
      if (onResolved) onResolved();
    } catch {
      toast.error("Couldn't dismiss. Please try again.");
      setIsClosing(false);
    }
  };

  const handleStart = async () => {
    // Mark complete server-side (fire-and-forget), then bubble to parent
    fetch(`${API}/athlete/missions/${mission.mission_id}/complete`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
    if (onStart) onStart();
    if (onResolved) onResolved();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        className="relative bg-gradient-to-br from-primary/20 via-surface to-surface border border-primary/40 rounded-xl p-5 sm:p-6 overflow-hidden"
        data-testid="mission-card"
      >
        {/* Subtle accent */}
        <div className="absolute -top-8 -right-8 w-40 h-40 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Target className="w-5 h-5 text-primary" />
            </div>
            <p className="text-primary text-xs font-bold uppercase tracking-widest">
              {title}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-text-muted hover:text-white p-1 -mt-1 -mr-1 rounded-lg hover:bg-white/5 transition-colors"
            aria-label="Dismiss"
            data-testid="mission-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p
          className="text-white text-base sm:text-lg leading-relaxed mb-4 relative"
          data-testid="mission-body"
        >
          {mission.body}
        </p>

        <div className="flex items-center gap-3 relative">
          <button
            onClick={handleStart}
            className="bg-primary hover:bg-blue-600 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors"
            data-testid="mission-start-btn"
          >
            {cta}
          </button>
          <button
            onClick={handleDismiss}
            className="text-text-secondary hover:text-white px-4 py-2.5 text-sm transition-colors"
            data-testid="mission-later-btn"
          >
            Later
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MissionCard;
